/**
 * Workflow types for Agent Brains CMS
 * Aligned with Agent Brains Go backend JSON tags
 */
import type { UserSummary } from "./api.types";

// ---------------------------------------------------------------------------
// Enums
// ---------------------------------------------------------------------------

export type ScheduleType = "manual" | "cron" | "once";
export type ExecutionMode = "linear" | "dag";
export type WorkflowKind = "agentic" | "etl" | "mixed";
export type RunPrincipalKind = "user" | "service";

export type RunStatus =
  | "pending"
  | "running"
  | "success"
  | "failed"
  | "cancelled"
  // BE-native paused statuses (from workflow_runs.status CHECK in mig 000017).
  // SSE stream synthesizes "paused_manual" / "paused_checkpoint" from
  // run_paused events with pausedReason; both wire shapes coexist.
  | "paused"
  | "waiting_checkpoint"
  | "paused_checkpoint"
  | "paused_manual";

export type StepStatus =
  | "pending"
  | "running"
  | "success"
  | "failed"
  | "skipped"
  | "cancelled"
  | "thinking"
  | "tool_running"
  | "waiting_checkpoint"
  | "waiting"
  | "paused"
  | "skipped_by_decision";

export type TriggerType = "scheduler" | "manual" | "api" | "retry" | "webhook";

export type StepType =
  | "extract"
  | "transform"
  | "load"
  | "filter"
  | "validate"
  | "lookup"
  | "aggregate"
  | "api_extract"
  | "api_load"
  | "http_request"
  | "notify"
  | "conditional"
  | "delay"
  | "code"
  | "subworkflow"
  // Agentic step types
  | "llm_call"
  | "agent_task"
  | "agent_decision"
  | "checkpoint"
  | "mcp_tool_call"
  | "kb_source"
  | "kb_output"
  | "kg_search"
  | "kg_neighbors"
  | "kg_context";

export type WorkflowCatalogItemType = StepType | "webhook_trigger";

/** Set of step types that belong to the agentic execution model. */
export const AGENTIC_STEP_TYPES = new Set<StepType>([
  "llm_call",
  "agent_task",
  "agent_decision",
  "checkpoint",
  "mcp_tool_call",
]);

// ---------------------------------------------------------------------------
// Entities
// ---------------------------------------------------------------------------

export interface Workflow {
  id: string;
  orgId: string;
  name: string;
  slug: string;
  description?: string;
  scheduleType: ScheduleType;
  cronExpression?: string;
  nextRunAt?: string;
  isEnabled: boolean;
  executionMode: ExecutionMode;
  createdBy: string;
  status: string;
  createdAt?: string;
  updatedAt?: string;
  steps?: WorkflowStep[];
  /** Embedded by BE when BE_EMBED_USERS=true (default). */
  createdByUser?: UserSummary | null;
  /** BE column (A1 schema). Preferred over FE derivation when present. */
  kind?: WorkflowKind;
  /** Folder this workflow belongs to; null = root-level. */
  folderId?: string | null;
  /** Cost of the most recent run; null when no runs exist yet. */
  lastRunCostUsd?: number | null;
  /** LLM use-cases required by this workflow's steps (derived by BE from step types).
   *  Absent on older API responses — treat as [] to avoid false disable. */
  requiredUseCases?: string[];
  /** Caller-scoped actions emitted by BE. Undefined means legacy/admin response. */
  effectiveActions?: string[];
  runAsPrincipalKind?: RunPrincipalKind;
  runAsPrincipalId?: string;
  runAsPrincipalUser?: UserSummary | null;
}

/** Aggregate metrics for a single workflow — sourced from GET /workflows/:id/stats. */
export interface WorkflowStats {
  runs24h: number;
  /** null when no completed runs with cost data exist. */
  avgCostUsd: number | null;
  /** P95 run duration in ms; null when fewer than 20 completed runs. */
  p95DurationMs: number | null;
  /** Average time-to-approval across all checkpoint runs; null when no approval checkpoints exist. */
  avgApprovalMs: number | null;
}

export interface WorkflowWebhookTrigger {
  id: string;
  workflowId: string;
  name: string;
  publicId: string;
  enabled: boolean;
  secretPrefix: string;
  secretRotatedAt: string;
  payloadLimitBytes: number;
  createdAt: string;
  updatedAt: string;
  secret?: string;
}

export interface CreateWorkflowWebhookPayload {
  name: string;
  enabled?: boolean;
  payloadLimitBytes?: number;
}

export interface UpdateWorkflowWebhookPayload {
  name?: string;
  enabled?: boolean;
  payloadLimitBytes?: number;
}

export interface WorkflowMCPTool {
  id: string;
  name: string;
  description?: string;
  parameters?: Record<string, unknown>;
  resourceType?: string;
}

export interface WorkflowNodeCatalog {
  catalogVersion: string;
  nodes: WorkflowNodeType[];
}

export interface WorkflowNodeType {
  type: WorkflowCatalogItemType;
  displayName: string;
  description: string;
  category: string;
  icon: string;
  schemaVersion: string;
  configSchema: WorkflowJSONSchema;
  configUiSchema: WorkflowNodeConfigUISchema;
  inputOutput: {
    inputs: WorkflowNodeDataPort[];
    outputs: WorkflowNodeDataPort[];
  };
  resourceRequirements: WorkflowNodeResourceRequirement[];
  riskClass: string;
  features: string[];
  capabilities: string[];
  executable: boolean;
  runtimeStatus: string;
  authoringNotes?: string[];
}

export interface WorkflowJSONSchema {
  type?: string;
  description?: string;
  default?: unknown;
  enum?: string[];
  format?: string;
  required?: string[];
  properties?: Record<string, WorkflowJSONSchema>;
  items?: WorkflowJSONSchema;
  additionalProperties?: boolean | WorkflowJSONSchema;
}

export interface WorkflowNodeConfigUISchema {
  layout?: string;
  order?: string[];
}

export interface WorkflowNodeDataPort {
  name: string;
  dataType: string;
  description: string;
}

export interface WorkflowNodeResourceRequirement {
  kind: string;
  selectorPath: string;
  actions: string[];
  tableRefStrategy?: string;
  policy?: string;
  optional?: boolean;
}

export interface WorkflowStep {
  id: string;
  workflowId: string;
  stepOrder: number;
  stepType: StepType;
  stepName: string;
  connectorId?: string;
  config: Record<string, unknown>;
  uiPosition?: Record<string, unknown>;
  inputStepIds?: string[];
  createdBy?: string;
  createdAt?: string;
  updatedAt?: string;
}

export interface WorkflowRun {
  id: string;
  workflowId: string;
  status: RunStatus;
  startedAt?: string;
  finishedAt?: string;
  durationMs?: number;
  totalItemsProcessed: number;
  incrementalState?: Record<string, unknown>;
  workflowContext?: Record<string, unknown>;
  errorMessage?: string;
  errorStepId?: string;
  triggeredBy: TriggerType;
  triggeredByUser?: string;
  createdAt?: string;
  steps?: WorkflowRunStep[];
  /** Embedded by BE when BE_EMBED_USERS=true. Maps to RunResponse.TriggeredByUserObject. */
  triggeredByUserObject?: UserSummary | null;
  /**
   * When non-null, this run was created via POST /workflow-runs/:id/retry
   * on the referenced source run. UI renders a "Retried from #X" badge
   * + click-through to the source detail page.
   */
  retriedFromRunId?: string | null;
  /** Embedded by run detail; paginated endpoint remains source for longer logs. */
  events?: RunEvent[];
  eventsTotal?: number;
}

export interface WorkflowRunStep {
  id: string;
  runId: string;
  stepId: string;
  status: StepStatus;
  progressPercent: number;
  progressMessage?: string;
  startedAt?: string;
  finishedAt?: string;
  durationMs?: number;
  inputCount?: number;
  outputCount?: number;
  errorMessage?: string;
  errorDetails?: Record<string, unknown>;
  metadata?: Record<string, unknown>;
  inputData?: Record<string, unknown>;
  outputData?: Record<string, unknown>;
  createdAt?: string;
  updatedAt?: string;
  step?: WorkflowStep;
}

// ---------------------------------------------------------------------------
// CRUD payloads
// ---------------------------------------------------------------------------

export interface CreateWorkflowPayload {
  name: string;
  description?: string;
  scheduleType?: ScheduleType;
  cronExpression?: string;
  isEnabled?: boolean;
  executionMode?: ExecutionMode;
  runAsPrincipalKind?: RunPrincipalKind;
  runAsPrincipalId?: string;
}

export interface UpdateWorkflowPayload {
  name?: string;
  description?: string;
  scheduleType?: ScheduleType;
  cronExpression?: string;
  isEnabled?: boolean;
  executionMode?: ExecutionMode;
  status?: string;
  runAsPrincipalKind?: RunPrincipalKind | null;
  runAsPrincipalId?: string | null;
}

/** Partial update payload for persisting step uiPosition after drag. */
export interface UpdateWorkflowDefinitionPayload {
  definition: {
    steps: Array<{
      id: string;
      uiPosition: Record<string, unknown>;
    }>;
  };
}

// ---------------------------------------------------------------------------
// Filter params
// ---------------------------------------------------------------------------

export interface WorkflowFilters {
  status?: string;
  scheduleType?: ScheduleType;
  isEnabled?: boolean;
  search?: string;
  page?: number;
  limit?: number;
  /** Filter by folder; undefined = no filter, null = root-only (spec TBD). */
  folderId?: string | null;
  /** Filter by workflow kind (agentic / etl / mixed / all). */
  kind?: WorkflowKind | "";
}

// ---------------------------------------------------------------------------
// Agentic domain — events + checkpoints (used by A5.3 / A5.4)
// ---------------------------------------------------------------------------

/** A single domain event appended to the run event log. */
export interface RunEvent {
  id: string;
  runId: string;
  stepId?: string | null;
  eventType: string;
  payload: Record<string, unknown>;
  actorUserId?: string | null;
  actorUser?: UserSummary | null;
  createdAt: string;
}

export type CheckpointStatus = "pending" | "approved" | "rejected" | "expired" | "cancelled";
export type ApproverDecision = "approved" | "rejected";
export type ApproverPrincipalKind = "user" | "role" | "group";

/** Per-approver entry from GET /checkpoints/:id response. */
export interface CheckpointApprover {
  principalKind: ApproverPrincipalKind;
  principalId: string;
  /** Only present for kind=user — BE embeds via Preload convention. */
  approverUser?: UserSummary | null;
  isRequired: boolean;
  decision?: ApproverDecision | null;
  decidedAt?: string | null;
  comment?: string | null;
}

/** Human-approval checkpoint attached to a run. */
export interface Checkpoint {
  id: string;
  runId: string;
  workflowId: string;
  /** workflow name embedded by BE list mapper. */
  workflowName?: string;
  stepId: string;
  /** step name embedded by BE list mapper. */
  stepName?: string;
  /** step alias embedded by BE list mapper. */
  stepAlias?: string;
  title: string;
  description?: string | null;
  status: CheckpointStatus;
  approvalQuorum: number;
  approvedCount: number;
  rejectedCount: number;
  expiresAt?: string | null;
  requestedByUser?: UserSummary | null;
  formSchema?: Record<string, unknown> | null;
  formData?: Record<string, unknown> | null;
  isFormLocked: boolean;
  /** Current principal's decision — null if not yet decided. */
  myDecision?: ApproverDecision | null;
  /** Per-approver list — present in detail response only. */
  approvers?: CheckpointApprover[];
  cancelledByUserId?: string | null;
  cancelledAt?: string | null;
  cancellationReason?: string | null;
  createdAt?: string;
}

/** Filters for the checkpoint inbox query. */
export interface CheckpointFilters {
  status?: CheckpointStatus | "all";
  limit?: number;
  cursor?: string;
}

export interface RunFilters {
  status?: RunStatus;
  triggeredBy?: TriggerType;
  page?: number;
  limit?: number;
}

// ---------------------------------------------------------------------------
// KB / KG ref types — anticipates BE A3 payload additions
// ---------------------------------------------------------------------------

/** A knowledge-base source reference attached to an agent event payload. */
export interface KbRef {
  id: string;
  title?: string;
  /** Number of KB entries / chunks cited. */
  count?: number;
}

/** A knowledge-graph reference attached to an agent event payload. */
export interface KgRef {
  id: string;
  name?: string;
  entityCount?: number;
  relationCount?: number;
}

/** Typed payload for agent.decide.complete events. */
export interface AgentDecideCompletePayload {
  pickedBranch: string;
  reasoning?: string;
  confidence?: number;
  kbRefs?: KbRef[];
  kgRefs?: KgRef[];
}

/** Typed payload for step_text_complete events. */
export interface StepTextCompletePayload {
  text: string;
  kbRefs?: KbRef[];
  kgRefs?: KgRef[];
}
